import React from 'react'
import data from './data'
import { USAState } from './USAState'

export interface USAMapProps {
  onClick?: (event: React.BaseSyntheticEvent) => void
  width?: number
  height?: number
  defaultFill?: string
  customize?: {
    [key: string]: {
      fill?: string
      clickHandler?: (event: React.BaseSyntheticEvent) => void
    }
  }
  title?: string
  showLabels?: boolean
  hideLabels?: string[]
  labelOffsets?: Record<string, { x: number; y: number; isExternal?: boolean }>
  customFill?: Record<string, string> | Record<string, string>[]
}

type StateData = {
  abbreviation: string
  name: string
  dimensions: string
}

const mapData: Record<string, StateData> = data() as unknown as Record<string, StateData>

// Stable default references — avoids a new object identity on every render
const DEFAULT_ON_CLICK = () => {}
const DEFAULT_CUSTOMIZE: USAMapProps['customize'] = {}
const DEFAULT_HIDE_LABELS: string[] = []
const DEFAULT_LABEL_OFFSETS: USAMapProps['labelOffsets'] = {}

export const USAMap: React.FC<USAMapProps> = ({
  onClick = DEFAULT_ON_CLICK,
  width = 959,
  height = 593,
  defaultFill = '#D3D3D3',
  title = 'Blank US states map',
  customize = DEFAULT_CUSTOMIZE,
  showLabels = false,
  hideLabels = DEFAULT_HIDE_LABELS,
  labelOffsets = DEFAULT_LABEL_OFFSETS,
  customFill,
}) => {
  const fillStateColor = (state: string) => {
    if (customFill) {
      if (Array.isArray(customFill)) {
        for (const item of customFill) {
          if (item[state]) return item[state]
        }
      } else if (customFill[state]) {
        return customFill[state]
      }
    }

    if (customize && customize[state] && customize[state].fill) {
      return customize[state].fill
    }
    return defaultFill
  }

  const stateClickHandler = (state: string) => {
    if (customize && customize[state] && customize[state].clickHandler) {
      return customize[state].clickHandler
    }
    return onClick
  }

  const buildPaths = () => {
    const paths = []
    for (const stateKey in mapData) {
      const stateObj = mapData[stateKey]
      const path = (
        <USAState
          key={stateKey}
          stateName={stateObj.name}
          dimensions={stateObj.dimensions}
          state={stateKey}
          fill={fillStateColor(stateKey)}
          onClickState={stateClickHandler(stateKey)}
          showLabel={showLabels && !hideLabels.includes(stateKey)}
          labelOffsets={labelOffsets}
        />
      )
      paths.push(path)
    }
    return paths
  }

  return (
    <svg
      className="us-state-map"
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 959 593"
    >
      <title>{title}</title>
      <g className="outlines">
        {buildPaths()}
        {/* <g className="DC state">
          <path
            className="DC1"
            fill={fillStateColor('DC1')}
            d="M801.8,253.8 l-1.1-1.6 -1-0.8 1.1-1.6 2.2,1.5z"
          />
          <circle
            className="DC2 cursor-pointer"
            onClick={stateClickHandler('DC')}
            data-name="DC"
            fill={fillStateColor('DC2')}
            stroke="#FFFFFF"
            strokeWidth="1.5"
            cx="801.3"
            cy="251.8"
            r="5"
            opacity="1"
          />
          {showLabels && (
            <>
              <line
                x1={801.3}
                y1={251.8}
                x2={(labelOffsets['DC']?.x || 900) - 6}
                y2={labelOffsets['DC']?.y || 290}
                stroke="#1a1a1a"
                strokeWidth="0.8"
                className="pointer-events-none"
              />
              <text
                x={labelOffsets['DC']?.x || 900}
                y={labelOffsets['DC']?.y || 290}
                textAnchor="start"
                alignmentBaseline="central"
                className="pointer-events-none fill-black font-medium text-[10px] select-none"
                data-name="DC"
              >
                DC
              </text>
            </>
          )}
        </g> */}

        {/* Highlight squares for small states on the right side */}
        <g className="small-states-boxes">
          {['VT', 'NH', 'MA', 'RI', 'CT', 'NJ', 'DE', 'MD', 'DC'].map((stateCode, index) => {
            const startX = 900
            const startY = 220
            const boxSize = 15
            const gap = 24
            const yOffset = startY + index * gap
            return (
              <g
                key={`box-${stateCode}`}
                className={`${stateCode} state cursor-pointer`}
                onClick={stateClickHandler(stateCode)}
                data-name={stateCode}
              >
                <rect
                  x={startX}
                  y={yOffset}
                  width={boxSize}
                  height={boxSize}
                  fill={fillStateColor(stateCode)}
                  stroke="#E2E8F0"
                  strokeWidth="1.5"
                  className="transition-colors duration-200"
                  data-name={stateCode}
                />
                <text
                  x={startX + boxSize + 8}
                  y={yOffset + boxSize / 2}
                  alignmentBaseline="central"
                  className="pointer-events-none fill-black font-medium text-[12px] select-none"
                  data-name={stateCode}
                >
                  {stateCode}
                </text>
              </g>
            )
          })}
        </g>
      </g>
    </svg>
  )
}

export default USAMap
